import React, { FC } from "react";
import { Product } from "data/data";
import { MinusIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import NcImage from "shared/NcImage/NcImage";
import { useCartDataProvider } from "context/CartProductContext";
import { calculatingTotalPrice } from "utils/priceCalculations";
import { getSelectedModifierNames } from "utils/modifierHelpers";

export interface CartItemProps {
  item: Product;
  onEdit?: (item: Product) => void;
  compact?: boolean;
}

const CartItem: FC<CartItemProps> = ({ item, onEdit, compact = false }) => {
  const { dispatch } = useCartDataProvider();
  const { name, imageUrl, quantity = 1, modifierChecked, modifierSets } = item;

  const modifierNames =
    modifierSets && modifierChecked
      ? getSelectedModifierNames(item, modifierChecked)
      : [];

  const lineTotal = calculatingTotalPrice(item);

  return (
    <div
      className={`flex gap-3 ${
        compact ? "py-3" : "py-4"
      } border-b border-slate-100 dark:border-slate-700 last:border-b-0`}
    >
      {/* Image */}
      <div
        className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100 dark:bg-slate-700 cursor-pointer"
        onClick={() => onEdit?.(item)}
      >
        <NcImage
          containerClassName="w-full h-full"
          src={imageUrl || ""}
          className="object-cover w-full h-full"
        />
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h4
              className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate cursor-pointer"
              onClick={() => onEdit?.(item)}
            >
              {name}
            </h4>
            {/* Selected modifiers as chips */}
            {modifierNames.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {modifierNames.map((modName, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                  >
                    {modName}
                  </span>
                ))}
              </div>
            )}
            {item.note && (
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 truncate italic">
                Note: {item.note}
              </p>
            )}
          </div>

          {/* Price */}
          <span className="text-sm font-bold text-slate-900 dark:text-slate-100 whitespace-nowrap">
            ${lineTotal.toFixed(2)}
          </span>
        </div>

        {/* Quantity controls */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1">
            {quantity <= 1 ? (
              <button
                onClick={() =>
                  dispatch({ type: "REMOVE_ITEMS", payload: { cartItemId: item.cartItemId } })
                }
                className="w-8 h-8 flex items-center justify-center rounded-full border border-red-200 dark:border-red-800 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                aria-label="Remove item"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() =>
                  dispatch({ type: "HANDLE_DECREMENT", payload: { cartItemId: item.cartItemId } })
                }
                className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                aria-label="Decrease quantity"
              >
                <MinusIcon className="w-4 h-4" />
              </button>
            )}
            <span className="w-8 text-center text-sm font-semibold text-slate-900 dark:text-slate-100">
              {quantity}
            </span>
            <button
              onClick={() =>
                dispatch({ type: "HANDLE_INCREMENT", payload: { cartItemId: item.cartItemId } })
              }
              className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Increase quantity"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
